import { Box } from '@mui/material';
import { useCallback, useRef } from 'react';
import { useRecoilState } from 'recoil';
import type { Image3D } from '~/domain/patient/image3d';
import type { Environment } from '~/engine/data/Environment';
import { useElementSize } from '~/hooks/useElementSize';
import { petMipViewSettingsState } from '~/state/atoms/viewerAtoms';
import PetMipPane from './PetMipPane';

interface FittedPetMipPaneProps {
    readonly petImage: Image3D;
    readonly environment: Environment | undefined;
    readonly onLocate?: (voxelIndex: number) => void;
}

/**
 * The PET projection filling its measured box. Azimuth and polarity are
 * shared with the title-bar controls, so a snap there and a drag here turn
 * the same view.
 */
export default function FittedPetMipPane({
    petImage,
    environment,
    onLocate,
}: FittedPetMipPaneProps) {
    const boxRef = useRef<HTMLDivElement | null>(null);
    const size = useElementSize(boxRef);
    const [settings, setSettings] = useRecoilState(petMipViewSettingsState);
    const turn = useCallback(
        (deltaDeg: number) =>
            setSettings((current) => ({
                ...current,
                azimuthDeg: current.azimuthDeg + deltaDeg,
            })),
        [setSettings]
    );
    const widthPx = size ? Math.floor(size.widthPx) : 0;
    const heightPx = size ? Math.floor(size.heightPx) : 0;
    return (
        <Box
            ref={boxRef}
            data-testid="fitted-pet-mip-pane"
            sx={{ width: '100%', height: '100%', overflow: 'hidden' }}
        >
            {widthPx > 0 && heightPx > 0 ? (
                <PetMipPane
                    petImage={petImage}
                    environment={environment}
                    widthPx={widthPx}
                    heightPx={heightPx}
                    azimuthDeg={settings.azimuthDeg}
                    onTurn={turn}
                    isInverted={settings.isInverted}
                    onLocate={onLocate}
                />
            ) : null}
        </Box>
    );
}
